import { IExtendedBenefitsData } from '@/interfaces/cars.interfaces';

/**
 * Проверяет совместимость двух переданных опций выгоды
 * @param benefitA опция для проверки совместимости
 * @param benefitB опция для проверки совместимости
 * @returns возвращает true, если опции совместимы.
 */
function isBenefitsCompatible(
  benefitA: IExtendedBenefitsData,
  benefitB: IExtendedBenefitsData,
) {
  if (
    !Array.isArray(benefitA.compatibleWith) ||
    !Array.isArray(benefitB.compatibleWith)
  ) {
    return false;
  }

  const isOptionACompatible = benefitB.compatibleWith.some(
    compatibleOption => compatibleOption.id === benefitA.id,
  );

  const isOptionBCompatible = benefitA.compatibleWith.some(
    compatibleOption => compatibleOption.id === benefitB.id,
  );

  return isOptionACompatible && isOptionBCompatible;
}

/**
 * Возвращает список выгод, совместимых с текущей.
 *
 * Алгоритм использует массив объекта выгоды compatibleWith.
 * В массиве указаны идентификаторы выгод, совместимых с текущей.
 * Если существуют предварительно выбранные выгоды, то из совместимых
 * с текущей, исключаются варианты, которые не совместимы с предыдущими.
 *
 * Пример:
 * Выгода 1, совместима с 2 и 3.
 * Выгода 2 совместима только с 1.
 *
 * Выбираем выгоду 2. Остается доступен выбор выгоды 1.
 * Выбираем дополнительно выгоду 1, несмотря на то, что
 * выгода 1 совместима с выгодами 2 и 3, выгода 3 не считается совместимой.
 * Потому что она не совместима ранее выбранной выгодой 2.
 *
 * @param benefits список всех выгод для автомобиля
 * @param checkingBenefit элемент, относительно которого производится поиск совместимых выгод
 * @param selectedBenefits список выбранных пользователем выгод
 * @returns массив совместимых выгод.
 */
const getCompatibleBenefits = (
  benefits: Array<IExtendedBenefitsData>,
  checkingBenefit: IExtendedBenefitsData,
  selectedBenefits: Array<IExtendedBenefitsData>,
) => {
  // Собираем массив выгод, совместимых с текущей.
  const compatibleBenefits: Array<IExtendedBenefitsData> = benefits.filter(benefit =>
    isBenefitsCompatible(benefit, checkingBenefit),
  );

  if (!selectedBenefits.length) {
    return compatibleBenefits;
  }

  // Отфильтровываем выгоды, не совместимые с ранее выбранными
  const benefitsCompatibleWithSelected = compatibleBenefits.filter(compatibleBenefit => {
    const isCompatibleWithSelected = selectedBenefits.every(selectedBenefit => {
      // Среди совместимых выгод, будут уже выбранные выгоды.
      // Такие выгоды не совместимы сами с собой, но должны быть добавлены
      // в результирующий массив
      if (selectedBenefit.id === compatibleBenefit.id) {
        return true;
      }

      return isBenefitsCompatible(compatibleBenefit, selectedBenefit);
    });

    return isCompatibleWithSelected;
  });

  return benefitsCompatibleWithSelected;
};

export default getCompatibleBenefits;
